import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  CardActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Tooltip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import Papa from 'papaparse';
import { usePermissions } from '../hooks/usePermissions';
import vendorApi, { Vendor, CreateVendorDTO, UpdateVendorDTO, VendorType, BulkImportResult } from '../api/vendor';

// ── Types ─────────────────────────────────────────────────────────────────────

interface VendorFormData {
  registered_name: string;
  phone: string;
  vendor_type: VendorType;
  vendor_id: string;
  alias: string;
  address: string;
  gst: string;
  bank_account_name: string;
  bank_account_no: string;
  bank_ifsc_code: string;
}

const emptyForm: VendorFormData = {
  registered_name: '',
  phone: '',
  vendor_type: 'SUPPLIER',
  vendor_id: '',
  alias: '',
  address: '',
  gst: '',
  bank_account_name: '',
  bank_account_no: '',
  bank_ifsc_code: '',
};

const TYPE_LABEL: Record<VendorType, string> = {
  SUPPLIER: 'Supplier',
  JOB_WORK_VENDOR: 'Job Work',
  EXTERNAL: 'External',
  INTERNAL: 'Internal',
};
const TYPE_COLOR: Record<VendorType, 'primary' | 'secondary' | 'success' | 'default'> = {
  SUPPLIER: 'primary',
  JOB_WORK_VENDOR: 'secondary',
  EXTERNAL: 'default',
  INTERNAL: 'success',
};

// CSV column order used for both export and the blank template
const CSV_COLUMNS: readonly string[] = [
  'vendor_id',
  'registered_name',
  'alias',
  'phone',
  'gst',
  'address',
  'bank_account_name',
  'bank_account_no',
  'bank_ifsc_code',
  'vendor_type',
];

const VALID_VENDOR_TYPES: VendorType[] = ['SUPPLIER', 'JOB_WORK_VENDOR', 'EXTERNAL', 'INTERNAL'];

interface ParsedImportRow {
  rowIndex: number;       // 1-based row number for display
  data: CreateVendorDTO;
  errors: string[];
  isValid: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const VendorsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canAdd, canEdit, canDelete, canView } = usePermissions();
  // Import/Export is gated on a separate permission module
  const canImportExport = canView('vendors-import-export') || canAdd('vendors-import-export');

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'' | VendorType>('');

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<VendorFormData>({ ...emptyForm });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Vendor | null>(null);

  // Bulk selection + bulk type change
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkType, setBulkType] = useState<VendorType | ''>('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(displayed.map(v => v.id)) : new Set());
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleBulkTypeChange = async () => {
    if (!bulkType || selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map(id => vendorApi.update(id, { vendor_type: bulkType as VendorType }))
      );
      setSuccess(`${selectedIds.size} vendor(s) updated to ${TYPE_LABEL[bulkType as VendorType]}`);
      setSelectedIds(new Set());
      setBulkType('');
      fetchVendors();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update some vendors');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(0); // 0=upload, 1=preview, 2=result
  const [parsedRows, setParsedRows] = useState<ParsedImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await vendorApi.getAll();
      setVendors(resp.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // ── Derived list ────────────────────────────────────────────────────────────
  const displayed = vendors.filter((v) => {
    if (filterType && v.vendor_type !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        v.registered_name.toLowerCase().includes(q) ||
        (v.alias || '').toLowerCase().includes(q) ||
        v.phone.includes(q) ||
        (v.gst || '').toLowerCase().includes(q) ||
        (v.vendor_id || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ── Dialog helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (v: Vendor) => {
    setEditingId(v.id);
    setForm({
      registered_name: v.registered_name,
      phone: v.phone,
      vendor_type: v.vendor_type || 'SUPPLIER',
      vendor_id: v.vendor_id || '',
      alias: v.alias || '',
      address: v.address || '',
      gst: v.gst || '',
      bank_account_name: v.bank_account_name || '',
      bank_account_no: v.bank_account_no || '',
      bank_ifsc_code: v.bank_ifsc_code || '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setFormError(null); };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!form.registered_name.trim()) { setFormError('Registered name is required'); return; }
    if (!form.phone.trim()) { setFormError('Phone is required'); return; }

    const payload: CreateVendorDTO | UpdateVendorDTO = {
      registered_name: form.registered_name.trim(),
      phone: form.phone.trim(),
      vendor_type: form.vendor_type,
      vendor_id: form.vendor_id.trim() || undefined,
      alias: form.alias.trim() || undefined,
      address: form.address.trim() || undefined,
      gst: form.gst.trim() || undefined,
      bank_account_name: form.bank_account_name.trim() || undefined,
      bank_account_no: form.bank_account_no.trim() || undefined,
      bank_ifsc_code: form.bank_ifsc_code.trim() || undefined,
    };

    setSaving(true);
    try {
      if (editingId) {
        await vendorApi.update(editingId, payload as UpdateVendorDTO);
        setSuccess('Vendor updated');
      } else {
        await vendorApi.create(payload as CreateVendorDTO);
        setSuccess('Vendor added');
      }
      closeDialog();
      fetchVendors();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save vendor';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (v: Vendor) => { setToDelete(v); setDeleteOpen(true); };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await vendorApi.delete(toDelete.id);
      setSuccess('Vendor deleted');
      setDeleteOpen(false);
      setToDelete(null);
      fetchVendors();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete vendor');
      setDeleteOpen(false);
    }
  };

  // ── CSV Export ───────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const exportData = displayed.map((v) => ({
      vendor_id: v.vendor_id || '',
      registered_name: v.registered_name,
      alias: v.alias || '',
      phone: v.phone,
      gst: v.gst || '',
      address: v.address || '',
      bank_account_name: v.bank_account_name || '',
      bank_account_no: v.bank_account_no || '',
      bank_ifsc_code: v.bank_ifsc_code || '',
      vendor_type: v.vendor_type,
    }));

    const csv = Papa.unparse(exportData, { columns: CSV_COLUMNS as string[] });
    const today = new Date().toISOString().slice(0, 10);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vendors_export_${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── CSV Template Download ────────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    const csv = Papa.unparse([], { columns: CSV_COLUMNS as string[] });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'vendors_import_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Import dialog open/close ─────────────────────────────────────────────────
  const openImport = () => {
    setImportOpen(true);
    setImportStep(0);
    setParsedRows([]);
    setImportResult(null);
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportStep(0);
    setParsedRows([]);
    setImportResult(null);
  };

  // ── File parse (Step 0 → Step 1) ────────────────────────────────────────────
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows: ParsedImportRow[] = results.data.map((row, idx) => {
          const rowErrors: string[] = [];

          const registered_name = (row['registered_name'] || '').trim();
          const phone = (row['phone'] || '').trim();
          const vendor_type_raw = (row['vendor_type'] || '').trim();

          if (!registered_name) rowErrors.push('registered_name is required');
          if (!phone) rowErrors.push('phone is required');
          if (vendor_type_raw && !VALID_VENDOR_TYPES.includes(vendor_type_raw as VendorType)) {
            rowErrors.push(
              `vendor_type "${vendor_type_raw}" is invalid (use SUPPLIER, JOB_WORK_VENDOR, EXTERNAL, or INTERNAL)`
            );
          }

          const vendor_type: VendorType =
            vendor_type_raw && VALID_VENDOR_TYPES.includes(vendor_type_raw as VendorType)
              ? (vendor_type_raw as VendorType)
              : 'SUPPLIER';

          const data: CreateVendorDTO = {
            registered_name,
            phone,
            vendor_type,
            vendor_id: (row['vendor_id'] || '').trim() || undefined,
            alias: (row['alias'] || '').trim() || undefined,
            address: (row['address'] || '').trim() || undefined,
            gst: (row['gst'] || '').trim() || undefined,
            bank_account_name: (row['bank_account_name'] || '').trim() || undefined,
            bank_account_no: (row['bank_account_no'] || '').trim() || undefined,
            bank_ifsc_code: (row['bank_ifsc_code'] || '').trim() || undefined,
          };

          return { rowIndex: idx + 1, data, errors: rowErrors, isValid: rowErrors.length === 0 };
        });

        setParsedRows(rows);
        setImportStep(1);
        // Reset file input so same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      error: () => {
        setError('Failed to parse CSV file. Please ensure it is a valid CSV.');
      },
    });
  };

  // ── Confirm import (Step 1 → backend → Step 2) ──────────────────────────────
  const handleConfirmImport = async () => {
    const validRows = parsedRows.filter((r) => r.isValid).map((r) => r.data);
    if (validRows.length === 0) return;

    setImporting(true);
    try {
      const result = await vendorApi.bulkImport(validRows);
      setImportResult(result);
      setImportStep(2);
      fetchVendors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const validRowCount = parsedRows.filter((r) => r.isValid).length;
  const invalidRowCount = parsedRows.filter((r) => !r.isValid).length;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 3 }}>
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Vendors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage suppliers, job-work vendors and internal departments
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {canImportExport && (
              <>
                <Tooltip title={displayed.length === 0 ? 'No vendors to export' : `Export ${displayed.length} vendor(s) as CSV`}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportCSV}
                      disabled={displayed.length === 0}
                      size={isMobile ? 'small' : 'medium'}
                    >
                      Export CSV
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={openImport}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Import CSV
                </Button>
              </>
            )}
            {canAdd('vendors') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAdd}
                size={isMobile ? 'small' : 'medium'}
              >
                Add Vendor
              </Button>
            )}
          </Box>
        </Box>

        {/* Search + Type Filter */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name, alias, phone, GST..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value as '' | VendorType)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="SUPPLIER">Supplier</MenuItem>
              <MenuItem value="JOB_WORK_VENDOR">Job Work Vendor</MenuItem>
              <MenuItem value="EXTERNAL">External (Legacy)</MenuItem>
              <MenuItem value="INTERNAL">Internal (Legacy)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : displayed.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {search || filterType ? 'No vendors match the current filters.' : 'No vendors yet. Click "Add Vendor" to get started.'}
            </Typography>
          </Box>
        ) : isMobile ? (
          /* ── Mobile cards ─────────────────────────────────────────── */
          <Grid container spacing={1}>
            {displayed.map((v) => (
              <Grid item xs={12} key={v.id}>
                <Card variant="outlined">
                  <CardContent sx={{ pb: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>{v.registered_name}</Typography>
                        {v.alias && <Typography variant="caption" color="text.secondary">{v.alias}</Typography>}
                      </Box>
                      <Chip
                        size="small"
                        label={TYPE_LABEL[v.vendor_type] || v.vendor_type}
                        color={TYPE_COLOR[v.vendor_type] || 'default'}
                        variant="outlined"
                        sx={{ ml: 1, flexShrink: 0 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>📞 {v.phone}</Typography>
                    {v.gst && <Typography variant="body2" color="text.secondary">GST: {v.gst}</Typography>}
                    {v.vendor_id && <Typography variant="body2" color="text.secondary">ID: {v.vendor_id}</Typography>}
                  </CardContent>
                  <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
                    {canEdit('vendors') && (
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {canDelete('vendors') && (
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => confirmDelete(v)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* ── Desktop table ────────────────────────────────────────── */
          <>
            {/* Bulk action bar — shown when ≥1 row selected */}
            {selectedIds.size > 0 && canEdit('vendors') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 1, mb: 1, bgcolor: 'primary.50', borderRadius: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  {selectedIds.size} vendor(s) selected
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Change Type To</InputLabel>
                  <Select
                    label="Change Type To"
                    value={bulkType}
                    onChange={e => setBulkType(e.target.value as VendorType | '')}
                  >
                    <MenuItem value="SUPPLIER">Supplier</MenuItem>
                    <MenuItem value="JOB_WORK_VENDOR">Job Work Vendor</MenuItem>
                    <MenuItem value="EXTERNAL">External (Legacy)</MenuItem>
                    <MenuItem value="INTERNAL">Internal (Legacy)</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!bulkType || bulkUpdating}
                  onClick={handleBulkTypeChange}
                >
                  {bulkUpdating ? <CircularProgress size={16} /> : 'Apply'}
                </Button>
                <Button size="small" onClick={() => { setSelectedIds(new Set()); setBulkType(''); }}>
                  Clear Selection
                </Button>
              </Box>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {canEdit('vendors') && (
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Checkbox
                          size="small"
                          indeterminate={selectedIds.size > 0 && selectedIds.size < displayed.length}
                          checked={displayed.length > 0 && selectedIds.size === displayed.length}
                          onChange={e => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, width: 36 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Registered Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>GST</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Vendor ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Bank Details</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayed.map((v, idx) => (
                    <TableRow key={v.id} hover selected={selectedIds.has(v.id)}>
                      {canEdit('vendors') && (
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(v.id)}
                            onChange={e => handleSelectOne(v.id, e.target.checked)}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{v.registered_name}</Typography>
                        {v.alias && <Typography variant="caption" color="text.secondary">{v.alias}</Typography>}
                        {v.address && <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>{v.address}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={TYPE_LABEL[v.vendor_type] || v.vendor_type}
                          color={TYPE_COLOR[v.vendor_type] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{v.phone}</TableCell>
                      <TableCell>{v.gst || '—'}</TableCell>
                      <TableCell>{v.vendor_id || '—'}</TableCell>
                      <TableCell>
                        {v.bank_account_no ? (
                          <Typography variant="caption">
                            {v.bank_account_name && <>{v.bank_account_name}<br /></>}
                            A/C: {v.bank_account_no}
                            {v.bank_ifsc_code && ` | ${v.bank_ifsc_code}`}
                          </Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {canEdit('vendors') && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {canDelete('vendors') && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => confirmDelete(v)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* ── Add / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Registered Name" name="registered_name"
                value={form.registered_name} onChange={handleInput} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Phone" name="phone"
                value={form.phone} onChange={handleInput} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Vendor Type</InputLabel>
                <Select
                  value={form.vendor_type}
                  label="Vendor Type"
                  onChange={(e) => setForm(prev => ({ ...prev, vendor_type: e.target.value as VendorType }))}
                >
                  <MenuItem value="SUPPLIER">Supplier (Fabric / Material)</MenuItem>
                  <MenuItem value="JOB_WORK_VENDOR">Job Work Vendor</MenuItem>
                  <MenuItem value="EXTERNAL">External (Legacy)</MenuItem>
                  <MenuItem value="INTERNAL">Internal Department (Legacy)</MenuItem>
                </Select>
              </FormControl>
              {(form.vendor_type === 'JOB_WORK_VENDOR' || form.vendor_type === 'INTERNAL') && (
                <FormHelperText>Job work vendors appear in Job Work forms. Suppliers appear in Purchase &amp; PO forms.</FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Vendor ID (Optional)" name="vendor_id"
                value={form.vendor_id} onChange={handleInput} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Alias (Optional)" name="alias"
                value={form.alias} onChange={handleInput} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="GST (Optional)" name="gst"
                value={form.gst} onChange={handleInput} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Address (Optional)" name="address"
                value={form.address} onChange={handleInput} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Bank Account Name" name="bank_account_name"
                value={form.bank_account_name} onChange={handleInput} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Bank Account No" name="bank_account_no"
                value={form.bank_account_no} onChange={handleInput} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Bank IFSC Code" name="bank_ifsc_code"
                value={form.bank_ifsc_code} onChange={handleInput} />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingId ? 'Update' : 'Add Vendor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ───────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Vendor</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{toDelete?.registered_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Import CSV Dialog ─────────────────────────────────────────────── */}
      <Dialog open={importOpen} onClose={importStep < 2 ? closeImport : undefined} maxWidth="lg" fullWidth>
        <DialogTitle>Import Vendors from CSV</DialogTitle>
        <DialogContent>
          <Stepper activeStep={importStep} sx={{ mb: 3, mt: 1 }}>
            <Step><StepLabel>Upload File</StepLabel></Step>
            <Step><StepLabel>Preview &amp; Validate</StepLabel></Step>
            <Step><StepLabel>Result</StepLabel></Step>
          </Stepper>

          {/* ── Step 0: Upload ── */}
          {importStep === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" gutterBottom>
                Upload a CSV file to bulk create or update vendors.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Required columns:</strong> registered_name, phone
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Optional columns:</strong> vendor_id, alias, address, gst, bank_account_name, bank_account_no, bank_ifsc_code, vendor_type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>vendor_type values:</strong> SUPPLIER | JOB_WORK_VENDOR | EXTERNAL | INTERNAL
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                If a vendor with the same <em>registered_name</em> already exists, it will be <strong>updated</strong>. Otherwise a new vendor is <strong>created</strong>.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={handleDownloadTemplate}>
                  Download Template CSV
                </Button>
                <Button variant="contained" component="label" startIcon={<FileUploadIcon />}>
                  Choose CSV File
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={handleFileSelect}
                  />
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 1: Preview ── */}
          {importStep === 1 && (
            <Box>
              <Alert
                severity={invalidRowCount > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                <strong>{validRowCount}</strong> valid row(s) ready to import.
                {invalidRowCount > 0 && (
                  <> <strong>{invalidRowCount}</strong> row(s) have errors and will be skipped.</>
                )}
              </Alert>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 60 }}>Row</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 60 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Registered Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((row) => (
                      <TableRow
                        key={row.rowIndex}
                        sx={{
                          bgcolor: row.isValid ? 'success.50' : 'error.50',
                          '&:hover': { bgcolor: row.isValid ? '#e8f5e9' : '#ffebee' },
                        }}
                      >
                        <TableCell>{row.rowIndex}</TableCell>
                        <TableCell>
                          {row.isValid
                            ? <CheckCircleIcon color="success" fontSize="small" />
                            : <ErrorIcon color="error" fontSize="small" />}
                        </TableCell>
                        <TableCell>{row.data.registered_name || <em style={{ color: '#999' }}>—</em>}</TableCell>
                        <TableCell>{row.data.phone || <em style={{ color: '#999' }}>—</em>}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={TYPE_LABEL[row.data.vendor_type || 'SUPPLIER'] || row.data.vendor_type}
                            color={TYPE_COLOR[row.data.vendor_type || 'SUPPLIER'] || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {row.errors.length > 0 ? (
                            <Typography variant="caption" color="error.main">
                              {row.errors.join('; ')}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {importing && <LinearProgress sx={{ mt: 2 }} />}
            </Box>
          )}

          {/* ── Step 2: Result ── */}
          {importStep === 2 && importResult && (
            <Box sx={{ py: 2 }}>
              <Alert
                severity={importResult.errors.length > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                Import complete: <strong>{importResult.created}</strong> vendor(s) created,{' '}
                <strong>{importResult.updated}</strong> vendor(s) updated.
                {importResult.errors.length > 0 && (
                  <> {importResult.errors.length} row(s) had server-side errors (shown below).</>
                )}
              </Alert>
              {importResult.errors.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.errors.map((e, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{e.row}</TableCell>
                          <TableCell>{e.field}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="error.main">{e.message}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {importStep < 2 && (
            <Button onClick={closeImport} disabled={importing}>Cancel</Button>
          )}
          {importStep === 1 && (
            <>
              <Button
                onClick={() => { setImportStep(0); setParsedRows([]); }}
                disabled={importing}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmImport}
                disabled={importing || validRowCount === 0}
              >
                {importing
                  ? <CircularProgress size={20} />
                  : `Import ${validRowCount} valid row(s)`}
              </Button>
            </>
          )}
          {importStep === 2 && (
            <Button variant="contained" onClick={closeImport}>Done</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorsPage;
